import { apiClient as api } from './api';
import { callV3, type ApiResponse } from '../api/envelope';
import type {
  AssetFamilyResponse,
  AssetFolderHierarchy,
  AssetListResponse,
  GalleryResponse,
  ImageDetailsResponse,
  ImageUploadResponse,
  LifecycleState,
  UpdateLifecycleStateResult,
  UploadImageBase64Request,
  UserAssetsListResponse,
} from '../domain';

const OPERATIONS_BASE = '/api/v3/operations';
const ASSETS_BASE = '/api/v3/assets';

export interface ListGalleryParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  subType?: string;
}

export interface ListAssetsParams {
  appAgentType?: string;
  sessionId?: string;
  kind?: string;
  /** Path C (Slice 4): only assets the current user has favorited. */
  favorited?: boolean;
  /** Path C (Slice 4): filter by lifecycle. CSV for multi: "Finalized,Published". */
  lifecycleState?: string;
  /** Path C (Slice 4): derived assets (true) vs originals (false). */
  hasParent?: boolean;
  page?: number;
  pageSize?: number;
}

export const AssetService = {
  /**
   * Upload an image as multipart/form-data. The optional `lineage` arg writes
   * an `asset_relationships` row tying the new upload (child) back to
   * `parentAssetId` with the given type. Both `parentAssetId` and
   * `relationshipType` must be supplied together; mixing one with `undefined`
   * yields a BE-side validation error. Used by Image Lab's auto-bake path
   * to surface a `BakedFilters` derivative in the asset family graph.
   */
  uploadImage: (
    file: File,
    sessionId?: string,
    lineage?: { parentAssetId: string; relationshipType: string },
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    if (sessionId) formData.append('sessionId', sessionId);
    if (lineage) {
      formData.append('parentAssetId', lineage.parentAssetId);
      formData.append('relationshipType', lineage.relationshipType);
    }
    return callV3<ImageUploadResponse>(
      api.post<ApiResponse<ImageUploadResponse>>(`${OPERATIONS_BASE}/images`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    );
  },

  uploadImageBase64: (request: UploadImageBase64Request) =>
    callV3<ImageUploadResponse>(
      api.post<ApiResponse<ImageUploadResponse>>(`${OPERATIONS_BASE}/images/base64`, request),
    ),

  getImage: (imageId: string) =>
    callV3<ImageDetailsResponse>(
      api.get<ApiResponse<ImageDetailsResponse>>(`${OPERATIONS_BASE}/images/${imageId}`),
    ),

  deleteImage: async (imageId: string): Promise<void> => {
    await callV3<unknown>(
      api.delete<ApiResponse<unknown>>(`${OPERATIONS_BASE}/images/${imageId}`),
    );
  },

  getImageDownloadUrl: (imageId: string, format?: string, quality?: number): string => {
    const params = new URLSearchParams();
    if (format) params.append('format', format);
    if (quality !== undefined) params.append('quality', quality.toString());
    const qs = params.toString();
    return `${OPERATIONS_BASE}/images/${imageId}/download${qs ? `?${qs}` : ''}`;
  },

  // The download endpoint opts out of the envelope ([NoApiResponseEnvelope]),
  // so this bypasses callV3 and goes straight through axios.
  async downloadImage(imageId: string, filename: string, format?: string): Promise<void> {
    const response = await api.get(this.getImageDownloadUrl(imageId, format), {
      responseType: 'blob',
    });
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },

  listUserAssets: ({
    appAgentType,
    sessionId,
    kind,
    page = 1,
    pageSize = 20,
  }: ListAssetsParams = {}) =>
    callV3<UserAssetsListResponse>(
      api.get<ApiResponse<UserAssetsListResponse>>(`${OPERATIONS_BASE}/assets`, {
        params: { subType: appAgentType, sessionId, kind, page, pageSize },
      }),
    ),

  listGallery: ({
    page = 1,
    pageSize = 30,
    sortBy = 'updatedAt',
    sortOrder = 'desc',
    subType,
  }: ListGalleryParams = {}) =>
    callV3<GalleryResponse>(
      api.get<ApiResponse<GalleryResponse>>(`${OPERATIONS_BASE}/gallery`, {
        params: { page, pageSize, sortBy, sortOrder, subType },
      }),
    ),

  getFolders: () =>
    callV3<AssetFolderHierarchy>(
      api.get<ApiResponse<AssetFolderHierarchy>>(`${ASSETS_BASE}/folders`),
    ),

  list: ({
    appAgentType,
    sessionId,
    kind,
    favorited,
    lifecycleState,
    hasParent,
    page = 1,
    pageSize = 20,
  }: ListAssetsParams = {}) =>
    callV3<AssetListResponse>(
      api.get<ApiResponse<AssetListResponse>>(ASSETS_BASE, {
        params: {
          appAgentType,
          sessionId,
          kind,
          favorited,
          lifecycleState,
          hasParent,
          page,
          pageSize,
        },
      }),
    ),

  // ---------- Path C: lifecycle + lineage (Slice 4) ----------

  setLifecycleState: (assetId: string, state: LifecycleState | string) =>
    callV3<UpdateLifecycleStateResult>(
      api.patch<ApiResponse<UpdateLifecycleStateResult>>(
        `${ASSETS_BASE}/${assetId}/lifecycle-state`,
        { state },
      ),
    ),

  getFamily: (assetId: string, depth = 2) =>
    callV3<AssetFamilyResponse>(
      api.get<ApiResponse<AssetFamilyResponse>>(`${ASSETS_BASE}/${assetId}/family`, {
        params: { depth },
      }),
    ),
};
